// Tally Pro on the web (design/pro/mac-pro-tab-free.html): what Pro does and the plans. Pro belongs to an account, so a
// guest sees the way to one above the plans, and an order without one asks to sign in first. The web cannot take
// payments yet; signed in, the order points to the Tally app.
import { useEffect, useMemo, useRef, useState } from 'react';
import markWhite from '../../../design/assets/Logos/tellary-mark-white.svg';
import idleWhite from '../../../design/assets/Logos/tellary-mark-idle-white.svg';
import { switchGuest } from '../api/guest.ts';
import BorderGlow from '../components/BorderGlow.jsx';
import ClickSpark from '../components/ClickSpark.jsx';
import SpecularButton from '../components/SpecularButton.jsx';
import { Banner, ScreenHeader, TextInput } from '../components/common.tsx';
import { dayRange } from '../lib/calendar.ts';
import { runningEntry } from '../model/data.ts';
import { buildSnapshot } from '../model/snapshot.ts';
import { useMedia, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button, cx } from '../ui/ds.tsx';
import { Icon, type IconName } from '../ui/Icon.tsx';

type Plan = 'yearly' | 'monthly';

export function Pro() {
  const store = useStore();
  const t = store.t;
  const now = useNow(30_000);
  const today = useMemo(() => dayRange(now, store.timeZone), [Math.floor(now / 60_000), store.timeZone]);
  const data = useRange(today).data;
  const counting = data ? !!runningEntry(data, store.me?.workspace?.memberId) || buildSnapshot(store.ws, data, today, now).countingAgents > 0 : false;
  const guest = store.api.guest;
  const [plan, setPlan] = useState<Plan>('yearly');
  const outline = useRef<HTMLSpanElement>(null);
  const [answer, setAnswer] = useState<'account' | 'app' | 'active' | null>(null);
  const price = plan === 'yearly' ? '79 €' : '9 €';
  const toAccount = () => switchGuest(false);
  // Pro comes from the server alone (spec/pro.md): a guest needs an account first, a Pro account has it already.
  const order = () => setAnswer(guest ? 'account' : store.pro ? 'active' : 'app');
  // A developer code unlocks Pro for good (spec/pro.md §5); the server checks it and decides.
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const redeem = async () => {
    if (!code.trim() || redeeming) return;
    if (guest) { setAnswer('account'); return; }
    setRedeeming(true);
    setCodeError(null);
    try {
      await store.api.redeemProCode(code.trim());
      await store.refreshMe();
      setCode('');
      setAnswer('active');
    } catch (error) {
      setCodeError(store.describe(error));
    } finally {
      setRedeeming(false);
    }
  };
  const orderLabel = `${t('Pro abonnieren')} · ${price}`;
  const reduceMotion = useMedia('(prefers-reduced-motion: reduce)');
  const [hasWebGL] = useState(() => {
    try {
      const gl = document.createElement('canvas').getContext('webgl2');
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      return !!gl;
    } catch { return false; }
  });

  useEffect(() => {
    if (reduceMotion) outline.current?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
  }, [reduceMotion]);

  const selectPlan = (value: Plan, animate: boolean) => {
    setAnswer(null);
    const element = outline.current;
    if (!animate || reduceMotion) element?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
    if (value === plan) return;
    if (element && animate && !reduceMotion) {
      // Capture the visible shape before cancelling, so a quick reversal never jumps.
      const style = getComputedStyle(element);
      const start = { left: style.left, top: style.top, width: style.width, height: style.height };
      const halves = [...element.children];
      const clips = halves.map(half => getComputedStyle(half).clipPath);
      element.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      const timing = { duration: 760, easing: 'cubic-bezier(.4, 0, .2, 1)' };
      const target = { left: value === 'monthly' ? 'calc(50% + 5px)' : '0px', top: '0px', width: 'calc(50% - 5px)', height: '100%' };
      const gap = { left: 'calc(50% - 4px)', top: '0px', width: '8px', height: '100%' };
      // Retract the sides first. Vertical folding stays inside the gap, clear of the labels.
      element.animate([
        start,
        { ...start, top: '0px', height: '100%', offset: .14 },
        { ...gap, offset: .38 },
        { ...gap, top: '38%', height: '24%', offset: .5 },
        { ...gap, offset: .62 },
        { ...target, offset: .86 },
        target,
      ], timing);
      halves.forEach((half, index) => {
        const open = index === 0 ? 'inset(0% 0% 92% 0%)' : 'inset(92% 0% 0% 0%)';
        half.animate([
          { clipPath: clips[index] },
          { clipPath: open, offset: .14 },
          { clipPath: open, offset: .86 },
          { clipPath: index === 0 ? 'inset(0% 0% 50% 0%)' : 'inset(50% 0% 0% 0%)' },
        ], timing);
      });
    }
    setPlan(value);
  };

  const feature = (icon: IconName, title: string, detail: string) => (
    <div className="pro-feature">
      <span className="pro-feature__icon" aria-hidden="true"><Icon name={icon} size={20} /></span>
      <span className="pro-feature__text">
        <span className="pro-feature__title">{title}</span>
        <span className="pro-feature__detail">{detail}</span>
      </span>
    </div>
  );
  const choice = (value: Plan, label: string, amount: string, hint: string) => (
    <button type="button" role="radio" aria-checked={plan === value} tabIndex={plan === value ? 0 : -1} className={cx('pro-plan', plan === value && 'is-selected')}
      onClick={(e) => selectPlan(value, e.detail > 0)}
      onKeyDown={(e) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        const next = e.key === 'Home' ? 'yearly' : e.key === 'End' ? 'monthly' : value === 'yearly' ? 'monthly' : 'yearly';
        selectPlan(next, false);
        e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next === 'yearly' ? 0 : 1]?.focus();
      }}>
      <span className="pro-plan__label">{label}<span className="pro-plan__mark" aria-hidden="true">{plan === value ? <Icon name="check" size={12} /> : null}</span></span>
      <span className="pro-plan__price">{amount}</span>
      <span className="pro-plan__hint">{hint}</span>
    </button>
  );
  const compare = (title: string, free: boolean) => (
    <tr key={title}>
      <th scope="row">{title}</th>
      <td>{free ? <Icon name="check" size={16} /> : <span aria-hidden="true">–</span>}<span className="tl-sr">{free ? t('In Free und Pro enthalten') : t('In Pro enthalten')}</span></td>
      <td><Icon name="check" size={16} /></td>
    </tr>
  );

  const content = (
    <div className="screen pro-page">
      <ScreenHeader title="Tellary Pro" subtitle={t('Automatisieren. Auswerten. Abrechnen.')} />
      <BorderGlow
        className="pro-hero-glow"
        edgeSensitivity={30}
        glowColor="40 80 80"
        backgroundColor="#120F17"
        borderRadius={28}
        glowRadius={40}
        glowIntensity={1.0}
        coneSpread={25}
        animated={false}
        colors={['#c084fc', '#f472b6', '#38bdf8']}
      >
        <section className="pro-hero" aria-label="Tellary Pro">
          <div className="pro-hero__text">
            <span className="pro-hero__label">Tellary Pro</span>
            <h2>{t('Deine Arbeit.\nBereit zur Abrechnung.')}</h2>
            <p>{t('Regeln ordnen deine Agent-Arbeit zu, Stundensätze rechnen mit, und der Leistungsnachweis ist fertig, wenn du es bist.')}</p>
          </div>
          <img className="pro-hero__mark" src={counting ? markWhite : idleWhite} width={120} height={120} alt="" />
        </section>
      </BorderGlow>

      {guest ? (
        <section className="tl-card pro-account" data-testid="pro-account">
          <span className="pro-feature__icon" aria-hidden="true"><Icon name="user" size={20} /></span>
          <span className="pro-feature__text">
            <span className="pro-feature__title">{t('Pro gibt es nur mit Konto')}</span>
            <span className="pro-feature__detail">{t('Pro gilt für dein Tellary-Konto auf all deinen Geräten. Erstelle ein Konto oder melde dich an, bevor du Pro abonnierst. Als Gast nutzt du weiter alles aus Free.')}</span>
          </span>
          <span className="pro-account__actions">
            <Button variant="primary" onClick={toAccount}>{t('Jetzt Konto erstellen')}</Button>
            <Button onClick={toAccount}>{t('Anmelden')}</Button>
          </span>
        </section>
      ) : null}

      {/* The heading sits above both columns, so the features and the plans start on the same line. */}
      <div className="pro-grid">
        <h3 className="pro-grid__title">{t('Was Pro für dich tut')}</h3>
        <div className="tl-card pro-features">
          {feature('branch', t('Weniger zuordnen'), t('Regeln ordnen deine Agent-Arbeit automatisch Projekten zu.'))}
          {feature('doc', t('Einfach abrechnen'), t('Stundensätze, Leistungsnachweise und Rechnungen für deine Kunden.'))}
          {feature('chart', t('Genauer auswerten'), t('Zeiten und gemeldete Kosten nach Kunde, Projekt und Agent.'))}
        </div>
        <section className="pro-side" aria-label={t('Tarif wählen')}>
          <div className="tl-card pro-plans">
            <h3>{t('Tarif wählen')}</h3>
            <div className="pro-plans__choices" data-plan={plan} role="radiogroup" aria-label={t('Tarif wählen')}>
              {choice('yearly', t('Jährlich'), '79 €', t('%@ im Monat', '6,58 €'))}
              {choice('monthly', t('Monatlich'), '9 €', t('pro Monat'))}
              <span className="pro-plan-outline" ref={outline} aria-hidden="true"><span /><span /></span>
            </div>
            <div className="pro-order" data-testid="pro-order">
              {hasWebGL && !reduceMotion ? (
                <SpecularButton
                  size="lg"
                  radius={18}
                  tint="#ffffff"
                  tintOpacity={0}
                  blur={0}
                  textColor="#f5f5f5"
                  lineColor="#ffffff"
                  baseColor="#525252"
                  intensity={1}
                  shineSize={10}
                  shineFade={40}
                  thickness={1}
                  speed={0.35}
                  followMouse
                  proximity={0}
                  autoAnimate={false}
                  onClick={order}
                >
                  {orderLabel}
                </SpecularButton>
              ) : (
                <button type="button" className="specular-button specular-button--lg" onClick={order}>{orderLabel}</button>
              )}
            </div>
            <p className="pro-plans__caption">{plan === 'yearly'
              ? t('Abrechnung jährlich. Das Abo verlängert sich automatisch, bis du es kündigst.')
              : t('Abrechnung monatlich. Das Abo verlängert sich automatisch, bis du es kündigst.')}</p>
            {answer === 'account' ? (
              <Banner tone="danger" actions={<Button size="sm" variant="plain" onClick={toAccount}>{t('Konto erstellen oder anmelden')}</Button>}>
                {t('Bitte erstelle vor der Bestellung ein Konto oder melde dich an.')}
              </Banner>
            ) : answer === 'app' ? (
              <Banner tone="neutral" icon="clock">{t('Die Bestellung per Stripe ist noch nicht freigeschaltet.')}</Banner>
            ) : answer === 'active' ? (
              <Banner tone="neutral" icon="check">{t('Pro ist für dein Konto aktiv.')}</Banner>
            ) : null}
            {store.pro ? null : (
              <form className="pro-code" onSubmit={(e) => { e.preventDefault(); void redeem(); }}>
                <span className="pro-code__title">{t('Du hast einen Code?')}</span>
                <span className="pro-code__row">
                  <TextInput className="input--mono" value={code} aria-label={t('Code')} placeholder="TALLY-XXXXX-XXXXX-XXXXX-XXXXX"
                    autoCapitalize="characters" autoComplete="off" spellCheck={false} onChange={(e) => setCode(e.target.value)} data-testid="pro-code" />
                  <Button type="submit" size="sm" disabled={!code.trim() || redeeming}>{t('Einlösen')}</Button>
                </span>
                {codeError ? <Banner tone="danger">{codeError}</Banner> : null}
              </form>
            )}
          </div>
          <div className="pro-free">
            <Icon name="network" size={20} />
            <span className="pro-feature__text">
              <span className="pro-free__title">{t('Gerätesync bleibt kostenlos.')}</span>
              <span className="pro-feature__detail">{t('Auch nach dem Ende von Pro bleiben Erfassung, persönlicher Sync und Export verfügbar. Deine gespeicherten Daten bleiben erhalten.')}</span>
            </span>
          </div>
        </section>
      </div>

      <details className="pro-compare">
        <summary>{t('Free und Pro vergleichen')}</summary>
        <table>
          <thead><tr><td /><th scope="col">Free</th><th scope="col">Pro</th></tr></thead>
          <tbody>
            {compare(t('Alle persönlichen Geräte und Web'), true)}
            {compare(t('Sync, Offline-Erfassung und Verlauf'), true)}
            {compare(t('Eigene Zeit und alle fünf Agents'), true)}
            {compare(t('Unbegrenzte Projekte und Kunden'), true)}
            {compare(t('Korrekturen und einfache Zeitberichte'), true)}
            {compare(t('Automatische Projektzuordnung'), false)}
            {compare(t('Stundensätze und Abrechnungsregeln'), false)}
            {compare(t('Leistungsnachweise und Rechnungs-PDFs'), false)}
            {compare(t('Stripe-Rechnungen und Zahlungsstatus'), false)}
            {compare(t('Detaillierte Kostenberichte'), false)}
          </tbody>
        </table>
      </details>
      <p className="note">{t('Pro gilt für eine Person. Teamfunktionen, Budgets und Cloud-Sicherungen mit Versionen sind kein Bestandteil dieses Abos.')}</p>
    </div>
  );

  return (
    <div className="pro-click-spark">
      {reduceMotion ? content : (
        <ClickSpark sparkColor="#fff" sparkSize={10} sparkRadius={15} sparkCount={8} duration={400}>
          {content}
        </ClickSpark>
      )}
    </div>
  );
}
